var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { getHrefValue, getLinkProps, isNil, isString, mergeProps, shouldBrowserHandleFocus, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconCaretRight } from '@hawkins/icons/lib/iconCaretRight';
import { IconCheckboxSelected } from '@hawkins/icons/lib/iconCheckboxSelected';
import { IconCheckboxUnselected } from '@hawkins/icons/lib/iconCheckboxUnselected';
import { IconCheckmark } from '@hawkins/icons/lib/iconCheckmark';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { useCombineRefs, useId, useNestedMenu, usePress } from '../hooks';
import { ListItemWrapper } from '../listItemWrapper';
import { MenuContext } from '../menu';
import { InlineNotificationBadge } from '../notificationBadge';
import { Text } from '../text';
import { useMenuItemStyles } from './menuItem.styles';
export var MenuItemIconValue = {
    SPACER: ' ',
};
function isImageSrc(iconValue) {
    return isString(iconValue) && iconValue !== MenuItemIconValue.SPACER;
}
var displayName = 'MenuItem';
/**
 * A `<MenuItem>` represents an item within a menu. It may contain a nested menu.
 *
 * `<MenuItem>` is intended to be used as a child of menu container components like `<Menu>` and `<UserMenu>`.
 */
export var MenuItem = React.forwardRef(function (_a, refProp) {
    var _b, _c, _d, _e;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _f = useProps(displayName, props), _g = _f.badge, badge = _g === void 0 ? false : _g, badgeProps = _f.badgeProps, caretIcon = _f.caretIcon, caretIconProps = _f.caretIconProps, classesProp = _f.classes, className = _f.className, closeOnAnyClick = _f.closeOnAnyClick, _h = _f.compact, compact = _h === void 0 ? false : _h, component = _f.component, _j = _f.defaultNestedMenuOpen, defaultNestedMenuOpen = _j === void 0 ? false : _j, _k = _f.disableListItemWrapper, disableListItemWrapper = _k === void 0 ? false : _k, _l = _f.disabled, disabled = _l === void 0 ? false : _l, floatingUiProps = _f.floatingUiProps, href = _f.href, icon = _f.icon, iconProps = _f.iconProps, defaultId = _f.id, imageFallbackText = _f.imageFallbackText, imageProps = _f.imageProps, labelProp = _f.label, _m = _f.labelNoWrap, labelNoWrap = _m === void 0 ? false : _m, layout = _f.layout, listItemWrapperProps = _f.listItemWrapperProps, nestedMenuContent = _f.nestedMenuContent, nestedMenuOpen = _f.nestedMenuOpen, nestedMenuProps = _f.nestedMenuProps, onAnyClick = _f.onAnyClick, onClick = _f.onClick, rootProps = _f.rootProps, selectType = _f.selectType, _o = _f.selected, selected = _o === void 0 ? false : _o, styleProp = _f.style, subtitle = _f.subtitle, _p = _f.subtitleNoWrap, subtitleNoWrap = _p === void 0 ? false : _p, to = _f.to, restProps = __rest(_f, ["badge", "badgeProps", "caretIcon", "caretIconProps", "classes", "className", "closeOnAnyClick", "compact", "component", "defaultNestedMenuOpen", "disableListItemWrapper", "disabled", "floatingUiProps", "href", "icon", "iconProps", "id", "imageFallbackText", "imageProps", "label", "labelNoWrap", "layout", "listItemWrapperProps", "nestedMenuContent", "nestedMenuOpen", "nestedMenuProps", "onAnyClick", "onClick", "rootProps", "selectType", "selected", "style", "subtitle", "subtitleNoWrap", "to"]);
    var id = useId(displayName, { defaultId: defaultId });
    var hasNestedMenu = !!nestedMenuContent;
    var isMultiSelect = selectType === 'multi';
    var isSingleSelect = selectType === 'single';
    var label = labelProp !== null && labelProp !== void 0 ? labelProp : children;
    var CaretIcon = caretIcon || (hasNestedMenu ? IconCaretRight : undefined);
    var isAssumedLink = getHrefValue({ href: href, to: to }) !== null;
    var isClickable = !!onClick || isAssumedLink;
    var isHoverable = hasNestedMenu || isClickable;
    var Component = component || (isAssumedLink ? 'a' : 'li');
    // Context
    var open = React.useContext(MenuContext).open;
    var isInMenu = open !== null;
    // Styles
    var baseClasses = useMenuItemStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var labelContainerClassName = clsx(classes.labelContainer, (_b = {}, _b[classes.labelContainerCompact] = compact, _b));
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root, (_c = {},
        _c[classes.rootClickable] = isClickable,
        _c[classes.rootCompact] = compact,
        _c[classes.rootDisabled] = disabled,
        _c[classes.rootEnabled] = !disabled,
        _c[classes.rootHoverable] = isHoverable,
        _c));
    var selectionIconClassName = clsx(classes.selectionIcon, (_d = {},
        _d[classes.selectionIconMultiSelected] = isMultiSelect && selected,
        _d[classes.selectionIconMultiUnselected] = isMultiSelect && !selected,
        _d[classes.selectionIconSingle] = isSingleSelect,
        _d));
    var selectionIconBackdropClassName = clsx(classes.selectionIconBackdrop, (_e = {},
        _e[classes.selectionIconBackdropDisabled] = disabled,
        _e));
    var _q = useNestedMenu({
        classes: classes,
        closeOnAnyClick: closeOnAnyClick,
        defaultNestedMenuOpen: defaultNestedMenuOpen,
        disabled: disabled,
        isClickable: isClickable,
        nestedMenuContent: nestedMenuContent,
        nestedMenuOpen: nestedMenuOpen,
        nestedMenuProps: nestedMenuProps,
        onAnyClick: onAnyClick,
        onClick: onClick,
        parentItemId: id,
        floatingUiProps: floatingUiProps,
    }), derivedNestedMenuOpen = _q.derivedNestedMenuOpen, isInNestedMenu = _q.isInNestedMenu, nestedMenuNode = _q.nestedMenuNode, onClickAugmented = _q.onClickAugmented, rootCallbacks = _q.rootCallbacks, rootRef = _q.rootRef;
    var pressProps = usePress({
        alwaysHandleClick: isAssumedLink && disabled,
        disabled: disabled,
        onPress: onClickAugmented,
    }).pressProps;
    // Refs
    var internalRef = React.useRef(null);
    var ref = useCombineRefs([internalRef, refProp, rootRef]);
    // State
    var _r = __read(React.useState(false), 2), didImageFail = _r[0], setDidImageFail = _r[1];
    var onImageError = React.useCallback(function () { return setDidImageFail(true); }, []);
    // Reset the image failure state if the `src` value has changed.
    React.useEffect(function () {
        if (isImageSrc(icon))
            setDidImageFail(false);
    }, [icon]);
    // Content
    var SelectionIcon;
    var iconNode;
    if (isSingleSelect && selected) {
        SelectionIcon = IconCheckmark;
    }
    else if (isMultiSelect) {
        SelectionIcon = selected ? IconCheckboxSelected : IconCheckboxUnselected;
    }
    if (icon === MenuItemIconValue.SPACER) {
        iconNode = _jsx("div", { className: classes.iconSpacer });
    }
    else if (isImageSrc(icon)) {
        iconNode = didImageFail ? (_jsx("div", __assign({ className: classes.imageFallback }, { children: !!imageFallbackText && (_jsx(Text, __assign({ bold: true, className: classes.imageFallbackText, noWrap: true, variant: "label" }, { children: imageFallbackText }))) }))) : (_jsx("img", __assign({ "aria-hidden": true, className: classes.image, alt: "", onError: onImageError, src: icon }, imageProps)));
    }
    else if (React.isValidElement(icon)) {
        iconNode = icon;
    }
    else if (icon) {
        var Icon = icon;
        var _s = iconProps || {}, iconClassName = _s.className, iconPropsRest = __rest(_s, ["className"]);
        iconNode = _jsx(Icon, __assign({ "aria-hidden": true, className: clsx(classes.icon, iconClassName) }, iconPropsRest));
    }
    return (_jsx(ListItemWrapper, __assign({ disabled: disableListItemWrapper, presentation: true, rootRef: internalRef }, listItemWrapperProps, { children: _jsxs(Component, __assign({ "aria-checked": selectType ? selected : undefined, "aria-disabled": disabled || undefined, "aria-expanded": hasNestedMenu ? derivedNestedMenuOpen : undefined, "aria-haspopup": hasNestedMenu ? 'menu' : undefined, className: rootClassName, id: id, ref: ref, role: selectType === 'single' ? 'menuitemradio' : selectType === 'multi' ? 'menuitemcheckbox' : 'menuitem', style: style, 
            // When inside a Hawkins <Menu> or nested menu, all menu items should be focusable (even if disabled) but NOT tabbable.
            // When outside a <Menu> (although this kind of usage is not encouraged),
            // make the menu item tabbable unless it is an element that the browser should handle tabbing behavior for.
            tabIndex: isInMenu || isInNestedMenu
                ? -1
                : isHoverable && !disabled && !isAssumedLink && !shouldBrowserHandleFocus(Component)
                    ? 0
                    : undefined }, getLinkProps(Component, { href: href, to: to }), mergeProps(mergeProps(__assign(__assign({}, restProps), rootProps), rootCallbacks), pressProps), { children: [!!selectType && (_jsx("div", __assign({ className: classes.selectionIconContainer }, { children: SelectionIcon && (_jsxs(_Fragment, { children: [
                            // TODO(hack): Is there a way to fix the icon so we don't need this hack?
                            isMultiSelect && selected && _jsx("div", { className: selectionIconBackdropClassName }), _jsx(SelectionIcon, { className: selectionIconClassName, size: "small" })] })) }))), iconNode && _jsx("div", __assign({ className: classes.iconContainer }, { children: iconNode })), !isNil(label) && (_jsx(Text, __assign({ className: labelContainerClassName, colorVariant: disabled ? 'disabled' : 'default', component: "div", level: compact ? 1 : 0, noWrap: labelNoWrap, variant: compact ? 'label' : 'body' }, { children: label }))), !isNil(subtitle) && (_jsx(Text, __assign({ className: classes.subtitleContainer, colorVariant: disabled ? 'disabled' : 'faded', component: "div", noWrap: subtitleNoWrap, variant: "label" }, { children: subtitle }))), CaretIcon && (_jsx("div", __assign({ className: classes.caretContainer }, { children: _jsx(CaretIcon, __assign({ className: classes.caret, size: "small" }, caretIconProps)) }))), badge && _jsx(InlineNotificationBadge, __assign({}, badgeProps)), nestedMenuNode] })) })));
});
MenuItem.displayName = displayName;
